import { publicHttp } from '@/lib/http';

/**
 * Sanitized, unauthenticated view of a quote, resolved from a share-link token.
 * Mirrors `controller.sharedQuoteResponse` in the vizitpro-api swagger spec —
 * no IDs, no `cdn_url`, no sibling quotes.
 */
export interface SharedQuoteLineItem {
  description: string;
  amount: number;
}

export interface SharedQuoteDto {
  company_name: string;
  /** The business's logo, if one is set. */
  logo_url: string | null;
  customer_name: string;
  job_name: string;
  /** Render `accepted` / `declined` as read-only states — there is no
   *  accept/decline action over the share link. */
  status: 'pending' | 'accepted' | 'declined';
  /** Client-supplied; not guaranteed to equal the sum of `line_items`. */
  total_amount: number;
  /** Always an array; may be empty. Display-only. */
  line_items: SharedQuoteLineItem[];
  notes: string | null;
  sent_at: string | null;
  /** RFC 3339 UTC. */
  expires_at: string;
}

/**
 * Body for `POST /public/quotes/{token}/respond` — mirrors
 * `controller.sharedRespondQuoteRequest`. `responder_name` is optional free text
 * (max 200) recorded against the response.
 */
export interface SharedRespondQuoteRequest {
  status: 'accepted' | 'declined';
  responder_name?: string;
}

export function sharedQuoteQueryKey(token: string) {
  return ['public-quote', token] as const;
}

/**
 * Calls `GET /public/quotes/{token}`. No auth, no cookies — uses `publicHttp`
 * so no `x-jwt` header and no 401 redirect. `404` = unknown / revoked / expired
 * token; `429` = rate limited (~5 req/s per IP across `/public/*`). Fetch once
 * per page load; never poll.
 */
export async function getSharedQuote(token: string): Promise<SharedQuoteDto> {
  const { data } = await publicHttp.get<SharedQuoteDto>(
    `/public/quotes/${encodeURIComponent(token)}`,
  );
  return data;
}

/**
 * Calls `POST /public/quotes/{token}/respond`. No auth. Accepts or declines the
 * quote behind the token and returns the refreshed shared view. Accepting
 * auto-declines the job's sibling quotes and spends the link. `400` = bad
 * status, `404` = missing / revoked / expired token, `409` = already responded.
 */
export async function respondToSharedQuote(
  token: string,
  body: SharedRespondQuoteRequest,
): Promise<SharedQuoteDto> {
  const { data } = await publicHttp.post<SharedQuoteDto>(
    `/public/quotes/${encodeURIComponent(token)}/respond`,
    body,
  );
  return data;
}
